import '../styles/Post.css';
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import WheelLoader from '../components/WheelLoader';
import CommentsSection from '../components/CommentsSection';

const Post = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likesLoading, setLikesLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/^http:/, 'https:');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/publicacion/${id}`);
        const data = await res.json();
        setPost(data);
      } catch (error) {
        console.error("Error cargando publicación:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id, API_URL]);

  useEffect(() => {
    if (post && isAuthenticated && user?.id) fetchLikesData();
  }, [post, isAuthenticated, user?.id]);

  const fetchLikesData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/like/`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const allLikes = await response.json();
        const postLikes = allLikes.filter(like =>
          like.id_publicacion === parseInt(id) && (like.id_comentario === null || like.id_comentario === undefined)
        );
        setLikeCount(postLikes.length);
        setIsLiked(!!postLikes.find(like => like.id_usuario === user.id));
      }
    } catch (error) {
      console.error("Error cargando datos de likes:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)
      ) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getProcessedImages = () => {
    if (!post || !post.imagenes) return [];
    if (Array.isArray(post.imagenes) && post.imagenes[0] && typeof post.imagenes[0] === 'object') {
      return post.imagenes
        .filter(img => img.url_foto !== post.url_portada)
        .sort((a, b) => (a.numero_imagen || 0) - (b.numero_imagen || 0));
    }
    if (Array.isArray(post.imagenes) && typeof post.imagenes[0] === 'string') {
      return post.imagenes
        .filter(url => url !== post.url_portada)
        .map((url, index) => ({ url_foto: url, numero_imagen: index + 1 }));
    }
    return [];
  };

  const handleLike = async () => {
    if (!isAuthenticated || !user?.id) { alert('Debes iniciar sesión para dar like'); return; }
    if (likesLoading) return;
    setLikesLoading(true);
    try {
      const likeData = { id_usuario: user.id, id_publicacion: parseInt(id), id_comentario: null };
      const method = isLiked ? 'DELETE' : 'POST';
      const response = await fetch(`${API_URL}/api/v1/like/`, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(likeData)
      });
      if (response.ok) {
        setIsLiked(!isLiked);
        setLikeCount(prev => isLiked ? Math.max(0, prev - 1) : prev + 1);
      } else {
        await fetchLikesData();
      }
    } catch (error) {
      console.error('Error de red en like:', error);
      alert('Error de conexión. Intenta de nuevo.');
    } finally {
      setLikesLoading(false);
    }
  };

  const handleShare = async () => {
    const shareButton = document.querySelector('.share-button');
    if (shareButton) { shareButton.classList.add('sharing'); setTimeout(() => shareButton.classList.remove('sharing'), 500); }
    const url = window.location.href;
    const title = post ? `${post.titulo} - ${post.nombre_marca_vehiculo} ${post.year_vehiculo}` : 'Publicación';
    const text = post ? `Mira este ${post.nombre_categoria_vehiculo}: ${post.titulo}` : 'Mira esta publicación';
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); }
      catch (error) { if (error.name !== 'AbortError') fallbackShare(url); }
    } else { fallbackShare(url); }
  };

  const fallbackShare = (url) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(() => alert('¡Enlace copiado al portapapeles!')).catch(() => copyToClipboardFallback(url));
    } else { copyToClipboardFallback(url); }
  };

  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px';
    document.body.appendChild(textArea);
    textArea.focus(); textArea.select();
    try { document.execCommand('copy'); alert('¡Enlace copiado al portapapeles!'); }
    catch (error) { alert('No se pudo copiar el enlace. URL: ' + text); }
    document.body.removeChild(textArea);
  };

  const handleEdit = () => { setIsDropdownOpen(false); navigate(`/edit-post/${id}`); };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta publicación? Esta acción no se puede deshacer.')) {
      setIsDropdownOpen(false); return;
    }
    setDeleting(true); setIsDropdownOpen(false);
    try {
      if (!token) { alert('Debes estar autenticado para eliminar publicaciones'); return; }
      const response = await fetch(`${API_URL}/api/v1/publicacion/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) { alert('Publicación eliminada correctamente'); navigate('/'); }
      else {
        const errorData = await response.json();
        if (response.status === 403) alert('No tienes permiso para eliminar esta publicación');
        else if (response.status === 404) alert('Publicación no encontrada');
        else alert(errorData.detail || 'Error al eliminar la publicación');
      }
    } catch (error) {
      alert('Error de conexión. Por favor, intenta de nuevo.');
    } finally { setDeleting(false); }
  };

  const isOwner = () => !!(user && post && isAuthenticated && user.id === post.id_usuario);

  if (loading) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <WheelLoader />
    </div>
  );

  if (!post) return <div>No se encontró la publicación</div>;

  const processedImages = getProcessedImages();
  const paragraphs = post.descripcion ? post.descripcion.split("\n") : [];

  return (
    <div className="publication-container">
      {/* Overlay eliminando */}
      {deleting && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', textAlign: 'center' }}>
            <WheelLoader />
            <p style={{ marginTop: '1rem' }}>Eliminando publicación...</p>
          </div>
        </div>
      )}

      {/* Hero imagen portada */}
      <div className="cover-image-hero">
        {post.url_portada
          ? <img src={post.url_portada} alt={post.titulo} className="cover-image" />
          : <div className="image-placeholder" style={{ height: '100%' }}>Sin imagen</div>
        }
        <div className="cover-hero-overlay" />

        {/* Botones arriba a la derecha */}
        <div className="actions-section">
          <button
            className={`like-button ${isLiked ? 'liked' : ''} ${likesLoading ? 'loading' : ''}`}
            onClick={handleLike}
            disabled={likesLoading || !isAuthenticated}
            aria-label={isLiked ? 'Quitar like' : 'Dar like'}
            title={!isAuthenticated ? 'Inicia sesión para dar like' : (isLiked ? 'Quitar like' : 'Dar like')}
          >
            {likesLoading ? (
              <div className="like-spinner" style={{ width: '16px', height: '16px', border: '2px solid #ccc', borderTop: '2px solid #ff3040', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill={isLiked ? "#ff3040" : "none"} stroke={isLiked ? "#ff3040" : "white"} strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {likeCount > 0 && (
                  <span style={{ fontSize: '14px', marginLeft: '4px', color: isLiked ? '#ff3040' : 'white' }}>{likeCount}</span>
                )}
              </>
            )}
          </button>

          <button className="share-button" onClick={handleShare} aria-label="Compartir publicación">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>

          {isOwner() && (
            <div className="more-options-container" style={{ position: 'relative' }}>
              <button ref={buttonRef} className="more-options-button" onClick={() => setIsDropdownOpen(!isDropdownOpen)} aria-label="Más opciones" disabled={deleting}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
                </svg>
              </button>
              {isDropdownOpen && (
                <div ref={dropdownRef} className="dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', width: '128px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 50, padding: '4px 0' }}>
                  <button onClick={handleEdit} disabled={deleting} style={{ width: '100%', padding: '8px 16px', textAlign: 'left', fontSize: '14px', color: deleting ? '#9ca3af' : '#374151', background: 'none', border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={e => !deleting && (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="m18.5 2.5 3 3L12 15l-4 1 1-4z"/></svg>
                    Editar
                  </button>
                  <button onClick={handleDelete} disabled={deleting} style={{ width: '100%', padding: '8px 16px', textAlign: 'left', fontSize: '14px', color: deleting ? '#9ca3af' : '#dc2626', background: 'none', border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={e => !deleting && (e.currentTarget.style.backgroundColor = '#fef2f2')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="m19,6v14a2,2 0,0 1,-2,2H7a2,2 0,0 1,-2-2V6m3,0V4a2,2 0,0 1,2-2h4a2,2 0,0 1,2,2V6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                    {deleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Título y meta sobre la imagen */}
        <div className="post-header">
          <div className="title-section">
            <span className="title-text">{post.titulo}</span>
            <div className="vehicle-details">
              <span className="brand">{post.nombre_marca_vehiculo}</span>
              <span className="year">{post.year_vehiculo}</span>
            </div>
            <div className="user-info">Publicado por: <b>{post.nombre_usuario}</b></div>
          </div>
        </div>
      </div>

      {/* Contenido del artículo */}
      <div className="content-wrapper">
        <div className="post-body">
          <div className="description-section">
            {paragraphs.map((parrafo, idx) => (
              <div key={idx} style={{ marginBottom: "1rem" }}>
                <p className="description-text">{parrafo}</p>
                {processedImages[idx] && (
                  <img
                    src={processedImages[idx].url_foto}
                    alt={`Imagen ${processedImages[idx].numero_imagen} - ${post.titulo}`}
                    style={{ width: "100%", height: "auto", margin: "10px 0", borderRadius: "8px", display: 'block' }}
                    loading="lazy"
                  />
                )}
              </div>
            ))}
            {processedImages.length > paragraphs.length && (
              <div style={{ marginTop: "1rem" }}>
                {processedImages.slice(paragraphs.length).map((img, idx) => (
                  <img key={`extra-${idx}`} src={img.url_foto} alt={`Imagen ${img.numero_imagen} - ${post.titulo}`}
                    style={{ width: "100%", height: "auto", margin: "10px 0", borderRadius: "8px", display: 'block' }}
                    loading="lazy"
                  />
                ))}
              </div>
            )}
          </div>

          {post.detalle && (
            <div className="specs-section">
              <h3>Detalles</h3>
              <p style={{ whiteSpace: 'pre-line' }}>{post.detalle}</p>
            </div>
          )}
        </div>

        <CommentsSection postId={id} user={user} token={token} isAuthenticated={isAuthenticated} apiUrl={API_URL} />

        {post.url && (
          <div className="post-origin">Origen: <b>{post.url}</b></div>
        )}
      </div>
    </div>
  );
};

export default Post;
